import { useEffect, useState, createContext, useMemo, useLayoutEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Home from './layout/Home';
import About from './layout/About';
import OurWork from './layout/OurWork';
import ProjectDetail from './layout/ProjectDetail';
import BlogDetail from './layout/BlogDetail';
import BlogDirectoryDetail from './layout/BlogDirectoryDetail';
import NotFound from './layout/NotFound';
import LoadScript from './components/LoadScript';

// 1. Create the Loading Context
interface LoadingContextType {
  setIsPageReady: (ready: boolean) => void;
}
export const LoadingContext = createContext<LoadingContextType>({
  setIsPageReady: () => {},
});

const ExternalRedirect = ({ url }: { url: string }) => {
  useEffect(() => {
    window.location.href = url;
  }, [url]);
  return null;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isPageReady, setIsPageReady] = useState(false);
  const [minTimePassed, setMinTimePassed] = useState(false);
  const location = useLocation();

  // 2. Handle Route Changes
  useLayoutEffect(() => {
    // Show loader on route change
    setIsLoading(true);
    setIsPageReady(false);
    setMinTimePassed(false);

    // Lock A: Minimum 2-second timer
    const timer = setTimeout(() => {
      setMinTimePassed(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // 3. Check if both conditions are met to hide loader
  useEffect(() => {
    if (minTimePassed && isPageReady) {
      setIsLoading(false);
      
      // Force GSAP to refresh triggers once the loader is gone
      // and the layout is potentially final
      setTimeout(() => {
        if (window.ScrollTrigger) {
          window.ScrollTrigger.refresh();
        }
      }, 100); 
    }
  }, [minTimePassed, isPageReady]);

  const contextValue = useMemo(() => ({ setIsPageReady }), []);

  return (
    <LoadingContext.Provider value={contextValue}>
      <LoadScript />
      
      {/* Existing Preloader - Controlled by React State */}
      {isLoading && (
        <div className="preloader" style={{ display: 'flex', opacity: 1, visibility: 'visible' }}>
          <div className="loading">
            <div className="bar bar1"></div>
            <div className="bar bar2"></div>
            <div className="bar bar3"></div>
            <div className="bar bar4"></div>
            <div className="bar bar5"></div>
            <div className="bar bar6"></div>
            <div className="bar bar7"></div>
            <div className="bar bar8"></div>
          </div>
        </div>
      )}

      <Routes>
        <Route path='/' element={<Home />} />
        <Route path='/about' element={<About />} />
        <Route path='/our-work' element={<OurWork />} />
        <Route path='/our-work/blog/:slug' element={<BlogDetail />} />
        <Route path='/our-work/blog/directory/:slug' element={<BlogDirectoryDetail />} />
        <Route path='/our-work/portfolio/:projectId' element={<ProjectDetail />} />
        <Route path='/contactUs' element={<About />} />
        <Route path='/amdavadnxt' element={<ExternalRedirect url="https://sacesta-amc-project.vercel.app/" />} />
        <Route path='*' element={<NotFound />} />
      </Routes>
    </LoadingContext.Provider>
  );
}

export default App;
